import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NJIconButton } from '@engie-group/fluid-design-system-react';

export interface Size2MixRightSideModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    /** Rendered below the body inside the Nemo `ModalFooter` frame (divider + padding, right-aligned). */
    footer?: ReactNode;
    /** Panel width. Nemo: `30vw` (`w-[30vw]`). */
    width?: string;
    testId?: string;
}

/**
 * Nemo `RightSideModal` (Fluid v5 `nj-modal` docked right, full height) — measured on integration
 * 20-10-2026, Réseau > Import/export > « Importer un réseau »:
 * dialog 30vw × 100vh, header 8px 24px on grey-100 with a grey-300 bottom border, title `nj-text--lg`
 * semibold, `close` icon button (lg, secondary). The whole content scrolls (`overflow-y: auto`).
 */
export function Size2MixRightSideModal({ isOpen, onClose, title, children, footer, width = '30vw', testId }: Size2MixRightSideModalProps) {
    const titleId = useId();
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return undefined;
        const previous = document.activeElement as HTMLElement | null;
        dialogRef.current?.focus();
        const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            previous?.focus?.();
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050 }}>
            {/* Nemo uses data-backdrop="static": clicking the backdrop does not close the modal. */}
            <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.5)' }} />
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                data-testid={testId}
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width,
                    minWidth: 320,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    outline: 'none',
                    background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                    boxShadow: 'var(--nj-semantic-elevation-shadow-raised, -4px 0 12px rgba(0, 0, 0, 0.15))',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 48,
                        padding: '8px 24px',
                        flexShrink: 0,
                        background: 'var(--nj-core-color-grey-100)',
                        borderBottom: '1px solid var(--nj-core-color-grey-300)',
                    }}
                >
                    <p id={titleId} className="nj-text nj-text--lg" style={{ flex: 1, minWidth: 0, margin: 0, fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {title}
                    </p>
                    <NJIconButton icon="close" label="Fermer" variant="secondary" scale="lg" onClick={onClose} />
                </div>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    {children}
                </div>
                {footer && (
                    // Nemo `ModalFooter`: top divider, 16px / 24px padding.
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 'var(--nj-semantic-size-spacing-8)',
                            padding: 'var(--nj-semantic-size-spacing-16) var(--nj-semantic-size-spacing-24)',
                            borderTop: '1px solid var(--nj-core-color-grey-300)',
                        }}
                    >
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
}
